import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../../api/axios'
import endpoints from '../../../api/endpoints'
import { extractApiErrorMessage } from '../../../utils/apiError'
import type {
  TimeOffAllocation,
  TimeOffDayDetail,
  RequestStatus,
  AuditHistoryItem,
  EmployeeInfo,
  BalanceInfo,
  UpcomingApprovedHours,
  TimeOffRequestApiResponse,
} from '../../../types/timeOffRequestDetails'

// ============================================================================
// APPROVER-SPECIFIC TYPES (display model)
// ============================================================================

export interface RequestedDate {
  toReqDtlId: number
  requestType: string
  date: string
  day?: string
  times?: string
  startTime?: string
  endTime?: string

  // ADD THESE
  durationFlag?: string
  durationLabel?: string
  partialRefId?: string | null

  requestedHours: string | number
  timeOffAllocations?: TimeOffAllocation[]
  toSubTypeCd?: string | null
  isOverlapped?: boolean
}

export interface ApproverRequestDetails {
  // Request identifiers
  toReqId: string
  empNUID: string

  // Request date range
  startDate: string
  endDate: string
  isSingleDay: string

  // Employee information
  employee: EmployeeInfo

  // Request details by date
  requestedDates: RequestedDate[]

  // Balance information
  balance: BalanceInfo

  // Status history
  statuses: RequestStatus[]
  auditHistory: AuditHistoryItem[]

  // Approval permissions and context
  isPartialApprovalAllowed: boolean
  isFullApprovalAllowed: boolean
  isHoldAllowed: boolean
  isOverrideAllowed: boolean
  isPartialRetractAllowed: boolean
  upcomingApprovedHours: UpcomingApprovedHours

  // Audit metadata
  createdBy: string
  createdTime: string
  updatedBy: string
  updatedTime: string
  holdUntilMaxDate: string | null
  partialRefId: string | null

  timeOffAllocations : TimeOffAllocation[]
  manualReqRcvdDate?: string | null
}

// ============================================================================
// TRANSFORM: raw API response → display model
// ============================================================================

/**
 * Format time display based on day detail.
 * Returns time range if specific times exist, "Full Day" if full day flag set, empty string otherwise.
 */
const formatTimeDisplay = (dayDetail: TimeOffDayDetail): string => {
  if (dayDetail.startTime && dayDetail.endTime) {
    return `${dayDetail.startTime} - ${dayDetail.endTime}`
  }
  if (dayDetail.durationFlag === 'F') {
    return 'Full Day'
  }
  return ''
}

/**
 * Transforms raw API response to display model for approver view.
 * Builds requestedDates array from timeOffDetails record.
 */
const transformApproverApiResponse = (
  raw: TimeOffRequestApiResponse
): ApproverRequestDetails => {
  // Build requestedDates array from timeOffDetails record
  const requestedDates: RequestedDate[] = Object.values(
    raw.timeOffDetails ?? {}
  ).map((dayDetail: TimeOffDayDetail) => {
    const totalHours = (dayDetail.timeOffAllocations ?? []).reduce(
      (sum, a) => sum + (a.hours ?? 0),
      0
    )
    const dayName = new Date(dayDetail.day + 'T00:00:00').toLocaleDateString(
      'en-US',
      { weekday: 'long' }
    )
    const times = formatTimeDisplay(dayDetail)
  // <-- ADD HERE
  const uniqueToTypes = new Set(
    (dayDetail.timeOffAllocations ?? []).map(
      (allocation) => allocation.toTypeCd
    )
  )

  const requestType =
    uniqueToTypes.size > 1
      ? 'Multiple (Combined)'
      : [...uniqueToTypes][0] ?? ''

const durationLabel = (() => {
  if (dayDetail.durationFlag === 'P') {
    return 'Partial Day'
  }
  if (dayDetail.durationFlag === 'F') {
    return 'Full Day'
  }
  return undefined
})()
    return {
      toReqDtlId: dayDetail.toReqDtlId,
      requestType,
      date: dayDetail.day,
      day: dayName,
      times,
      startTime: dayDetail.startTime ?? undefined,
      endTime: dayDetail.endTime ?? undefined,
        // ADD THESE extra for tooltip
       durationFlag: dayDetail.durationFlag ?? undefined,
       durationLabel,
  partialRefId: dayDetail.partialRefId ?? null,
      requestedHours: totalHours,
      timeOffAllocations: dayDetail.timeOffAllocations,
      toSubTypeCd: dayDetail.timeOffAllocations?.[0]?.toSubTypeCd ?? null,
      isOverlapped: dayDetail.isOverlapped,
    }
  })

  return {
    toReqId: raw.toReqId,
    empNUID: raw.empNUID,
    startDate: raw.startDate,
    endDate: raw.endDate,
    isSingleDay: raw.isSingleDay,
    employee: raw.employee,
    requestedDates,
    balance: raw.balance,
    statuses: raw.statuses,
    auditHistory: raw.auditHistory,
    isPartialApprovalAllowed: raw.isPartialApprovalAllowed,
    isFullApprovalAllowed: raw.isFullApprovalAllowed,
    isHoldAllowed: raw.isHoldAllowed,
    isOverrideAllowed: raw.isOverrideAllowed,
    isPartialRetractAllowed: raw.isPartialRetractAllowed,
    upcomingApprovedHours: raw.upcomingApprovedHours,
    createdBy: raw.createdBy,
    createdTime: raw.createdTime,
    updatedBy: raw.updatedBy,
    updatedTime: raw.updatedTime,
    holdUntilMaxDate: raw?.holdUntilMaxDate,
    partialRefId: raw?.partialRefId,
    timeOffAllocations: Object.values(raw.timeOffDetails ?? {}).flatMap(
  (detail) => detail.timeOffAllocations ?? []
  ),
  manualReqRcvdDate: raw?.manualReqRcvdDate ?? null,
  }
}

// ============================================================================
// STATE TYPE
// ============================================================================

export interface ApproverRequestDetailsState {
  data: ApproverRequestDetails | null
  loading: boolean
  error: string | null
}

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: ApproverRequestDetailsState = {
  data: null,
  loading: false,
  error: null,
}

// ============================================================================
// ASYNC THUNKS
// ============================================================================

/**
 * Fetch a single request by torId with approver context.
 * Includes balance information, permission flags, and upcoming approved hours.
 * Uses the user's browser timezone dynamically.
 */
export const getApproverRequestByTorId = createAsyncThunk(
  'approverRequestDetails/getApproverRequestByTorId',
  async (torId: string, { rejectWithValue, signal }) => {
    try {
      // Get user's browser timezone dynamically (e.g., "Asia/Kolkata", "America/New_York")
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
      const url = endpoints.requester.getTimeOffRequestDetails(torId, {
        requestFrom: 'APPROVER',
        timezone: userTimeZone,
      })
      const response = await api.get(url, { signal })
      return transformApproverApiResponse(
        response.data as TimeOffRequestApiResponse
      )
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiErrorMessage(
          error,
          'Failed to fetch approver request details'
        )
      )
    }
  }
)

// ============================================================================
// SLICE
// ============================================================================

const approverRequestDetailsSlice = createSlice({
  name: 'approverRequestDetails',
  initialState,
  reducers: {
    /**
     * Clear request details from state.
     */
    clearApproverRequestDetails: (state) => {
      state.data = null
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getApproverRequestByTorId.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(getApproverRequestByTorId.fulfilled, (state, action) => {
      state.loading = false
      state.data = action.payload
    })
    builder.addCase(getApproverRequestByTorId.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload as string
    })
  },
})

export const { clearApproverRequestDetails } =
  approverRequestDetailsSlice.actions
export default approverRequestDetailsSlice.reducer
